import { GTMVehicleAttributes } from './types';
import { mapToGtmVehicleAttributes } from './utils';
import { inventoryInteractionEvent } from './actions/inventory/interaction';
import {
  createMeetingEvent,
  meetingInteractionEvent,
  scheduleMeetingInteractionEvent,
} from './actions/meeting';
import {
  saveNewVideoInteractionEvent,
  videoAttributeInteractionInteractionEvent,
  videoInteractonEvent,
  videoMessageInteractionEvent,
} from './actions/video';
import { useCallback, useRef, useState } from 'react';
import {
  GTMSaveNewVideoInteractionEventArguments,
  GTMVideoInteractonEventArguments,
  GTMVideoMessageInteractionActions,
  VideoAttributeInteractionInteractionEventArgs,
} from './actions/video/types';
import { getInventoryItems } from 'lib/api';
import { useCDK } from '../useCdk';
import {
  selectRecordingTypeEvent,
  recordingToolIntearctionEvent,
  stopRecordingEvent,
  startRecordingEvent,
  addVideoAttributeEvent,
  discardVideoEvent,
} from './actions/recording';
import { recentlyDeletedInteractionEvent } from './actions/profile';
import { newLeadAddedEvent } from './actions/customers';
import { teleprompterSettingsInteractionEvent } from './actions/teleprompter';
import { designSettingsChangedEvent } from './actions/design';
import { customAttributeInteractionsEvent } from './actions/customAttribute/interaction';
import { emailSettingsChangedEvent } from './actions/email';

type TrackVideoInteractionArgs = {
  vin?: string;
} & Omit<GTMVideoInteractonEventArguments, 'vehicleData'>;

type TrackGTMVideoMessageInteractionEvent =
  GTMVideoMessageInteractionActions & {
    vin?: string;
  };

export function useGTMAnalytics() {
  const { isCdkUser } = useCDK();

  const [isFetchingVin, setIsFetchingVin] = useState(false);

  // tiny cache so we don't spam the inventory API for the same VIN
  const lastVinRef = useRef<string | undefined>(undefined);
  const lastVehicleDataRef = useRef<GTMVehicleAttributes | undefined>(
    undefined
  );

  const getLatestVehicleData = useCallback(
    async (vin?: string): Promise<GTMVehicleAttributes | undefined> => {
      if (!vin) return undefined;

      if (lastVinRef.current === vin && lastVehicleDataRef.current) {
        return lastVehicleDataRef.current;
      }

      setIsFetchingVin(true);
      try {
        const data = await getInventoryItems({
          search: vin,
          soldFilter: '2',
        });
        const item = data?.items?.[0];
        const vehicleData = item ? mapToGtmVehicleAttributes(item) : undefined;

        lastVinRef.current = vin;
        lastVehicleDataRef.current = vehicleData;

        return vehicleData;
      } finally {
        setIsFetchingVin(false);
      }
    },
    []
  );

  const trackVideoInteractionEvent = useCallback(
    async ({ vin, ...rest }: TrackVideoInteractionArgs) => {
      const vehicleData = await getLatestVehicleData(vin);

      // WithIds variant: send just IDs
      if (Array.isArray(rest.videoIds) && rest.videoIds.length > 0) {
        return videoInteractonEvent({
          action: rest.action,
          videoIds: rest.videoIds,
        });
      }

      // Details variant: requires videoAttributes + vehicleData
      return videoInteractonEvent({
        action: rest.action,
        videoAttributes: rest.videoAttributes,
        vehicleData,
      });
    },
    [getLatestVehicleData]
  );

  const trackVideoAttributeInteraction = useCallback(
    async ({
      videoAttributes,
    }: VideoAttributeInteractionInteractionEventArgs) => {
      return videoAttributeInteractionInteractionEvent({
        videoAttributes,
      });
    },
    []
  );

  const trackSaveNewVideo = useCallback(
    async (newVideoArgs: GTMSaveNewVideoInteractionEventArguments) => {
      return saveNewVideoInteractionEvent(newVideoArgs);
    },
    [getLatestVehicleData]
  );

  const trackVideoMessageInteractionEvent = useCallback(
    async ({ vin, ...rest }: TrackGTMVideoMessageInteractionEvent) => {
      const vehicleData = await getLatestVehicleData(vin);

      return videoMessageInteractionEvent({
        ...rest,
        vehicleData,
      });
    },
    [getLatestVehicleData]
  );

  return {
    trackGTMVideoInteractionEvent: isCdkUser
      ? trackVideoInteractionEvent
      : undefined,
    trackGTMVideoMessageInteractionEvent: isCdkUser
      ? trackVideoMessageInteractionEvent
      : undefined,
    trackGTMSaveNewVideoInteractionEvent: isCdkUser
      ? trackSaveNewVideo
      : undefined,

    // needs to be implemented -> using fields from form
    trackGTMVideoAttributeInteractionInteractionEvent: isCdkUser
      ? trackVideoAttributeInteraction
      : undefined,

    // inventory
    trackGTMInventoryInteractionEvent: isCdkUser
      ? inventoryInteractionEvent
      : undefined,

    // meeting
    trackGTMMeetingInteractionEvent: isCdkUser
      ? meetingInteractionEvent
      : undefined,
    trackGTMScheduleMeetingInteractionEvent: isCdkUser
      ? scheduleMeetingInteractionEvent
      : undefined,
    trackGTMCreateMeetingEvent: isCdkUser ? createMeetingEvent : undefined,

    // recording
    trackGTMRecordingToolIntearctionEvent: isCdkUser
      ? recordingToolIntearctionEvent
      : undefined,
    trackGTMSelectRecordingTypeEvent: isCdkUser
      ? selectRecordingTypeEvent
      : undefined,

    trackGTMStopRecordingEvent: isCdkUser ? stopRecordingEvent : undefined,
    trackGTMStartRecordingEvent: isCdkUser ? startRecordingEvent : undefined,
    trackGTMAddVideoAttributeEvent: isCdkUser
      ? addVideoAttributeEvent
      : undefined,
    trackGTMDiscardVideoEvent: isCdkUser ? discardVideoEvent : undefined,
    // profile
    trackGTMRecentlyDeletedInteractionEvent: isCdkUser
      ? recentlyDeletedInteractionEvent
      : undefined,
    //customers
    trackGTMNewLeadAddedEvent: isCdkUser ? newLeadAddedEvent : undefined,

    // desing
    trackDesingSettingsChangedEvent: isCdkUser
      ? designSettingsChangedEvent
      : undefined,

    // custom video attributes
    trackCustomAttributeInteractionsEvent: isCdkUser
      ? customAttributeInteractionsEvent
      : undefined,

    // email
    trackEmailSettingsChangedEvent: isCdkUser
      ? emailSettingsChangedEvent
      : undefined,
    isFetchingVin: isFetchingVin,

    // teleprompter settings
    trackTeleprompterSettingsInteractionEvent: isCdkUser
      ? teleprompterSettingsInteractionEvent
      : undefined,
  };
}
